/**
 * The season/episode view behind the show sheet.
 *
 * Two sources have to be joined, because neither is sufficient alone:
 *
 *   /sync/all-items  — carries `seasons[].episodes[].watched_at`, and is the
 *                      only place per-episode watched state exists.
 *   /tv/episodes/{id} — carries titles and air dates, and is the only place
 *                      the *full* episode list exists.
 *
 * The library payload lists only episodes the user has watched, so absence
 * there means unwatched rather than nonexistent. Presence is the signal; the
 * episode list defines what exists.
 */
import type { LibraryShow } from "@/api/schemas";
import type { Episode } from "@/lib/airtime";

export interface SeasonEpisode extends Episode {
  watched: boolean;
}

export interface SeasonView {
  number: number;
  label: string;
  episodes: SeasonEpisode[];
  watchedCount: number;
  /** Episodes already out. A season of nothing but future episodes cannot be marked. */
  airedCount: number;
}

function key(season: number, episode: number): string {
  return `${season}:${episode}`;
}

/**
 * Whether Simkl is withholding the per-episode detail for a finished show.
 *
 * `/sync/all-items/shows/completed` carries no `seasons` array at all — zero
 * of 121 items on a real account — while the watching list does. So the
 * moment a show is finished, the only record of which episodes were watched
 * disappears, and the sheet drew every season as empty. Marking the last
 * season of a show made all of its seasons appear to unmark themselves.
 *
 * The counts are still there, and they are unambiguous: everything aired has
 * been watched.
 */
export function detailWithheld(show: LibraryShow): boolean {
  if (show.seasons.length > 0) return false;
  const aired = show.totalEpisodes - show.notAiredEpisodes;
  return aired > 0 && show.watchedEpisodes >= aired;
}

/** The set of episodes Simkl records as watched, as "season:episode" keys. */
export function watchedKeys(show: LibraryShow): Set<string> {
  const keys = new Set<string>();
  for (const season of show.seasons) {
    for (const episode of season.episodes) {
      keys.add(key(season.number, episode.number));
    }
  }
  return keys;
}

export function buildSeasons(show: LibraryShow, episodes: readonly Episode[]): SeasonView[] {
  const watched = watchedKeys(show);
  const allWatched = detailWithheld(show);
  const bySeason = new Map<number, SeasonEpisode[]>();

  for (const episode of episodes) {
    const list = bySeason.get(episode.season) ?? [];
    list.push({
      ...episode,
      watched: allWatched ? episode.aired : watched.has(key(episode.season, episode.episode)),
    });
    bySeason.set(episode.season, list);
  }

  return [...bySeason]
    .toSorted(([a], [b]) => a - b)
    .map(([number, list]) => {
      const sorted = list.toSorted((a, b) => a.episode - b.episode);
      return {
        number,
        // Season 0 is Simkl's specials bucket, and "Season 0" reads as a bug.
        label: number === 0 ? "Specials" : `Season ${number}`,
        episodes: sorted,
        watchedCount: sorted.filter((episode) => episode.watched).length,
        airedCount: sorted.filter((episode) => episode.aired).length,
      };
    });
}

export type SeasonState = "none" | "part" | "all";

export function seasonState(season: SeasonView): SeasonState {
  if (season.airedCount > 0 && season.watchedCount >= season.airedCount) return "all";
  return season.watchedCount > 0 ? "part" : "none";
}

export interface EpisodeTargetRef {
  simklId: number;
  season: number;
  episode: number;
}

function flatten(seasons: readonly SeasonView[]): SeasonEpisode[] {
  const all: SeasonEpisode[] = [];
  for (const season of seasons) {
    // Specials sit outside the run order, so setting a position in season 3
    // must not sweep them up.
    if (season.number === 0) continue;
    all.push(...season.episodes);
  }
  return all;
}

function before(a: SeasonEpisode, b: SeasonEpisode): boolean {
  return a.season === b.season ? a.episode < b.episode : a.season < b.season;
}

/**
 * Every aired, unwatched episode up to and including `target`.
 *
 * This is the "I got this far" action. The TV Time import carried statuses but
 * no episode positions, so for 46 shows the only way back to a correct library
 * is to say where the user actually is, once, per show.
 */
export function throughEpisode(
  seasons: readonly SeasonView[],
  simklId: number,
  target: SeasonEpisode,
): EpisodeTargetRef[] {
  return flatten(seasons)
    .filter(
      (episode) =>
        episode.aired &&
        !episode.watched &&
        (before(episode, target) ||
          (episode.season === target.season && episode.episode === target.episode)),
    )
    .map((episode) => ({ simklId, season: episode.season, episode: episode.episode }));
}

/**
 * Every aired, unwatched episode up to and including the end of `season`.
 *
 * Marking season 8 means the first eight seasons are behind you, which is what
 * the episode rows have always meant and what the season button now means too.
 * Nobody watches a show from season 8.
 */
export function throughSeason(
  seasons: readonly SeasonView[],
  simklId: number,
  season: SeasonView,
): EpisodeTargetRef[] {
  return flatten(seasons)
    .filter((episode) => episode.aired && !episode.watched && episode.season <= season.number)
    .map((episode) => ({ simklId, season: episode.season, episode: episode.episode }));
}

/** Every watched episode in one season: the season button, undoing itself. */
export function inSeason(simklId: number, season: SeasonView): EpisodeTargetRef[] {
  return season.episodes
    .filter((episode) => episode.watched)
    .map((episode) => ({ simklId, season: episode.season, episode: episode.episode }));
}

/** Every watched episode from `target` onward: the same action, corrected downward. */
export function fromEpisode(
  seasons: readonly SeasonView[],
  simklId: number,
  target: SeasonEpisode,
): EpisodeTargetRef[] {
  return flatten(seasons)
    .filter((episode) => episode.watched && !before(episode, target))
    .map((episode) => ({ simklId, season: episode.season, episode: episode.episode }));
}
